import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AlertTriangle, Clock, User, Eye, X, Mail, MessageSquare } from 'lucide-react';

const TrainerReportWarningsManager = () => {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const openModal = (trainer) => {
    setSelectedTrainer(trainer);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedTrainer(null), 300);
  };

  useEffect(() => {
    fetchTrainerWarnings();
  }, []);

  const fetchTrainerWarnings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/trainer-warnings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('=== TRAINER WARNINGS DATA ===');
        console.log('Total trainers:', data.trainers?.length);
        
        // Log first trainer's reports in detail
        if (data.trainers && data.trainers.length > 0) {
          const firstTrainer = data.trainers[0];
          console.log('\n=== FIRST TRAINER ===');
          console.log('Name:', firstTrainer.trainer_name);
          console.log('Total reports:', firstTrainer.reports?.length);
          
          if (firstTrainer.reports && firstTrainer.reports.length > 0) {
            console.log('\n=== FIRST REPORT DETAILS ===');
            const firstReport = firstTrainer.reports[0];
            console.log('Full report object:', firstReport);
            console.log('Description field:', firstReport.description);
            console.log('Description type:', typeof firstReport.description);
            console.log('Description length:', firstReport.description?.length);
            console.log('Description trimmed:', firstReport.description?.trim());
            console.log('Will show message?', firstReport.description && firstReport.description.trim() !== '');
            console.log('Will show "No message"?', !firstReport.description || firstReport.description.trim() === '');
          }
        }
        
        setTrainers(data.trainers);
      }
    } catch (error) {
      console.error('Error fetching trainer warnings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getReasonLabel = (reason) => {
    const labels = {
      'inappropriate_content': 'Inappropriate Content',
      'spam': 'Spam',
      'harassment': 'Harassment',
      'fake_profile': 'Fake Profile',
      'other': 'Other'
    };
    return labels[reason] || reason;
  };

  const getSeverityColor = (count) => {
    if (count >= 5) return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300 dark:border-red-700';
    if (count >= 3) return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-300 dark:border-orange-700';
    return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700';
  };

  const getSeverityBadge = (count) => {
    if (count >= 5) return <Badge variant="destructive" className="ml-2">Critical</Badge>;
    if (count >= 3) return <Badge variant="warning" className="ml-2 bg-orange-500">Warning</Badge>;
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading trainer warnings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Trainer Report Warnings</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Trainers with 3+ reviewed reports in the last 30 days
          </p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {trainers.length} Trainer{trainers.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {trainers.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-3xl">✅</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">No Warnings</h3>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  No trainers currently have warning levels
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trainers.map((trainer) => (
            <Card key={trainer.trainer_id} className={`border-2 ${getSeverityColor(trainer.report_count)} hover:shadow-lg transition-shadow`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="flex items-center gap-2 text-base truncate dark:text-white">
                      <User className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{trainer.trainer_name || 'Unknown Trainer'}</span>
                    </CardTitle>
                    {getSeverityBadge(trainer.report_count)}
                  </div>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1 mt-2">
                  <p className="truncate" title={trainer.trainer_email}>
                    <Mail className="w-3 h-3 inline mr-1" />
                    {trainer.trainer_email}
                  </p>
                  {trainer.last_report_date && (
                    <p className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(trainer.last_report_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {trainer.report_count} Reports
                    </Badge>
                  </div>
                  
                  <Button 
                    onClick={() => openModal(trainer)}
                    className="w-full"
                    variant="outline"
                    size="sm"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View All Reports
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal for viewing all reports */}
      {showModal && selectedTrainer && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200"
          onClick={closeModal}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`p-6 border-b ${getSeverityColor(selectedTrainer.report_count)}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold dark:text-white flex items-center gap-2">
                    <User className="w-6 h-6" />
                    {selectedTrainer.trainer_name || 'Unknown Trainer'}
                    {getSeverityBadge(selectedTrainer.report_count)}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    {selectedTrainer.trainer_email}
                  </p>
                  <div className="flex items-center gap-4 mt-2">
                    <Badge variant="outline" className="dark:border-gray-600 dark:text-gray-300">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {selectedTrainer.report_count} Total Reports
                    </Badge>
                    {selectedTrainer.last_report_date && (
                      <span className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Last: {new Date(selectedTrainer.last_report_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content - Scrollable Reports */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              <h3 className="font-semibold dark:text-white text-lg mb-4">All Reports ({selectedTrainer.reports.length})</h3>
              <div className="space-y-4">
                {selectedTrainer.reports.map((report, idx) => (
                  <div
                    key={report.id}
                    className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="secondary" className="text-xs">
                            {getReasonLabel(report.reason)}
                          </Badge>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Report #{idx + 1}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                          <p>
                            <strong>Reported by:</strong> {report.reporter_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {report.reporter_email}
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                        <p>{new Date(report.created_at || report.resolved_at).toLocaleDateString()}</p>
                        <p>{new Date(report.created_at || report.resolved_at).toLocaleTimeString()}</p>
                      </div>
                    </div>
                    
                    {report.description && report.description.trim() !== '' ? (
                      <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border-l-4 border-orange-400">
                        <div className="flex items-start gap-2">
                          <MessageSquare className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Report Message:</p>
                            <p className="text-sm text-gray-800">{report.description}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 p-2 bg-gray-100 rounded text-xs text-gray-500 dark:text-gray-400 italic">
                        No message provided
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50 dark:bg-gray-700">
              <Button onClick={closeModal} className="w-full" variant="outline">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainerReportWarningsManager;
